/**
 * Nhật ký thay đổi toàn đơn vị (Doc/11a, góc nhìn "Theo người" + "Xuất ra"): ai đã làm gì, khi nào,
 * ở đâu, ai cho phép. Chỉ đọc. Phạm vi (đơn vị + địa bàn) do máy chủ áp (R7).
 *
 * Bộ lọc nằm trên URL để gửi được liên kết cho người khác và tải lại không mất. Dùng
 * `replaceState` vì đổi hash sẽ dựng lại cả màn và làm mất tiêu điểm ở ô đang chọn.
 *
 * Mã đề xuất chỉ để đối chiếu, chưa bấm được: Hàng chờ duyệt chỉ mở đề xuất đang chờ, còn mọi
 * thông tin của đề xuất đã quyết định đã hiện ngay trên dòng.
 */
import * as api from '../api-client.js';
import { toast, esc, formatDateTime, buildHash, parseHash, downloadBlob, el } from '../ui/util.js';
import { ENTITY_LABEL, shortId } from '../ui/proposal-fields.js';
import {
  METHOD_FILTER,
  filtersFromParams,
  gpsText,
  methodInfo,
  recordPath,
  rowsOf,
  toApiQuery,
  valueText,
} from '../ui/change-log-logic.js';

const PATH = '/change-log';
const COLUMNS = 8;

const options = (pairs, selected, emptyLabel) =>
  `<option value="">${esc(emptyLabel)}</option>${pairs
    .map(([value, label]) => `<option value="${esc(value)}"${value === selected ? ' selected' : ''}>${esc(label)}</option>`)
    .join('')}`;

function entryRows(entry) {
  const link = recordPath(entry);
  const label = esc(entry.entityLabel || 'Bản ghi không còn trong danh mục');
  const method = methodInfo(entry.method);
  const rows = rowsOf(entry);
  const span = rows.length > 1 ? ` rowspan="${rows.length}"` : '';
  const gps = gpsText(entry.gps);

  const shared = {
    head: `
      <td class="nowrap"${span}>${esc(formatDateTime(entry.changedAt))}</td>
      <td${span}>
        <span class="hint-text">${esc(ENTITY_LABEL[entry.entity] || entry.entity)}</span><br>
        ${link ? `<a href="${buildHash(link)}">${label}</a>` : label}
        ${entry.recordActive === false ? '<span class="hint-text"> (đã xoá)</span>' : ''}
      </td>`,
    tail: `
      <td${span}>
        <span class="badge status-${method.tone}">${esc(method.label)}</span>
        ${entry.reason ? `<div class="log-note">${esc(entry.reason)}</div>` : ''}
      </td>
      <td${span}>
        ${esc(entry.actor?.fullName || 'Không rõ')}
        ${gps ? `<div class="log-note mono" title="Vị trí lúc thực hiện">${esc(gps)}</div>` : ''}
      </td>
      <td${span}>
        ${esc(entry.approver?.fullName || '—')}
        ${entry.decisionNote ? `<div class="log-note">${esc(entry.decisionNote)}</div>` : ''}
      </td>
      <td${span}>
        ${
          entry.changeRequestId
            ? `<span class="mono" title="${esc(entry.changeRequestId)}">${esc(shortId(entry.changeRequestId))}</span>
               ${entry.requestedAt ? `<div class="log-note">Gửi ${esc(formatDateTime(entry.requestedAt))}</div>` : ''}`
            : '—'
        }
      </td>`,
  };

  return rows
    .map((change, i) => {
      const cells = change
        ? `<td>${esc(change.label || change.field)}</td>
           <td>
             <span class="change-old">${esc(valueText(change.old))}</span>
             <span class="ms ms-sm">arrow_forward</span>
             <span class="change-new">${esc(valueText(change.new))}</span>
           </td>`
        : '<td>—</td><td>—</td>';
      return `<tr class="${entry.method === 'proposal_rejected' ? 'log-rejected' : ''}">
        ${i === 0 ? shared.head : ''}${cells}${i === 0 ? shared.tail : ''}
      </tr>`;
    })
    .join('');
}

export async function renderMasterDataChanges(container) {
  const isSystemAdmin = api.getCurrentUser()?.role === 'admin';
  const { params } = parseHash();
  const filters = filtersFromParams(params);
  let currentPage = Number(params.page) > 0 ? Number(params.page) : 1;

  let donVis = [];
  if (isSystemAdmin) {
    try {
      donVis = await api.instances.list();
    } catch (err) {
      console.warn('[change-log] không tải được danh sách đơn vị', err);
    }
  }

  container.innerHTML = `
    <div class="page-head">
      <div><h1>Nhật ký thay đổi</h1><p>Ai đã làm gì, khi nào, ai cho phép. Nhật ký chỉ ghi thêm, không ai sửa hay xoá được.</p></div>
      <div class="page-head-actions">
        <button class="btn btn-tonal" id="logExport"><span class="ms">table_view</span>Xuất CSV</button>
      </div>
    </div>
    <div class="filters-bar">
      ${
        isSystemAdmin
          ? `<select data-filter="instanceId" aria-label="Lọc theo đơn vị điều tra">
               ${options(donVis.map((d) => [d.id, d.name]), filters.instanceId, 'Tất cả đơn vị')}
             </select>`
          : ''
      }
      <select data-filter="method" aria-label="Lọc theo cách thay đổi">${options(METHOD_FILTER, filters.method, 'Mọi cách thay đổi')}</select>
      <select data-filter="entity" aria-label="Lọc theo đối tượng">${options(Object.entries(ENTITY_LABEL), filters.entity, 'Mọi đối tượng')}</select>
      <select data-filter="field" aria-label="Lọc theo trường"></select>
      <select data-filter="actorId" aria-label="Lọc theo người thực hiện"></select>
      <select data-filter="approverId" aria-label="Lọc theo người duyệt"></select>
      <label class="filters-date">Từ <input type="date" data-filter="from" value="${esc(filters.from || '')}"></label>
      <label class="filters-date">Đến <input type="date" data-filter="to" value="${esc(filters.to || '')}"></label>
      <button class="btn btn-text sm" id="logClear" type="button">Xoá lọc</button>
    </div>
    <div class="card">
      <table class="list-table log-table">
        <thead><tr><th>Thời điểm</th><th>Đối tượng</th><th>Trường</th><th>Cũ → Mới</th><th>Cách</th><th>Người thực hiện</th><th>Người duyệt</th><th>Đề xuất</th></tr></thead>
        <tbody id="logBody"></tbody>
      </table>
      <div id="logEmpty" class="empty-state" hidden><span class="ms ms-xl">manage_history</span><p></p></div>
      <div id="logPaging" class="paging"></div>
    </div>`;

  const body = container.querySelector('#logBody');
  const empty = container.querySelector('#logEmpty');
  const select = (key) => container.querySelector(`[data-filter="${key}"]`);

  function syncUrl() {
    history.replaceState(null, '', buildHash(PATH, { ...filters, page: currentPage > 1 ? currentPage : undefined }));
  }

  /** Người và trường lấy từ chính nhật ký trong phạm vi, nên đổi đơn vị thì phải nạp lại. */
  async function loadFilterOptions() {
    let f = { actors: [], approvers: [], fields: [] };
    try {
      f = await api.masterDataChanges.filters({ instanceId: filters.instanceId });
    } catch (err) {
      console.warn('[change-log] không tải được lựa chọn lọc', err);
    }
    // Giá trị trên URL có thể không còn trong danh sách (liên kết cũ): giữ để lọc vẫn đúng như URL nói.
    const withCurrent = (pairs, key) =>
      filters[key] && !pairs.some(([v]) => v === filters[key]) ? [...pairs, [filters[key], filters[key]]] : pairs;
    const person = (p) => [p.id, p.fullName];
    select('field').innerHTML = options(withCurrent(f.fields.map((x) => [x.field, x.label]), 'field'), filters.field, 'Mọi trường');
    select('actorId').innerHTML = options(withCurrent(f.actors.map(person), 'actorId'), filters.actorId, 'Mọi người thực hiện');
    select('approverId').innerHTML = options(withCurrent(f.approvers.map(person), 'approverId'), filters.approverId, 'Mọi người duyệt');
  }

  function renderPaging(meta) {
    const host = container.querySelector('#logPaging');
    host.innerHTML = '';
    if (meta.totalPages <= 1) return;
    const prev = el('button', 'btn btn-text sm', 'Trước');
    prev.disabled = meta.page <= 1;
    prev.addEventListener('click', () => load(meta.page - 1));
    const info = el('span', 'paging-info', `Trang ${meta.page}/${meta.totalPages} (${meta.total} thay đổi)`);
    const next = el('button', 'btn btn-text sm', 'Sau');
    next.disabled = meta.page >= meta.totalPages;
    next.addEventListener('click', () => load(meta.page + 1));
    host.append(prev, info, next);
  }

  let requestSeq = 0;
  async function load(page) {
    const seq = ++requestSeq;
    currentPage = page;
    syncUrl();
    body.innerHTML = `<tr><td colspan="${COLUMNS}">Đang tải...</td></tr>`;
    empty.hidden = true;
    try {
      const res = await api.masterDataChanges.list({ ...toApiQuery(filters), page, pageSize: 30 });
      // Đổi lọc nhanh: câu trả lời cũ về sau không được ghi đè kết quả mới.
      if (seq !== requestSeq) return;
      // Liên kết cũ trỏ tới trang đã vượt quá tổng: lùi về trang cuối thay vì báo "không có gì".
      if (page > res.meta.totalPages && res.meta.total > 0) return load(res.meta.totalPages);
      body.innerHTML = res.data.map(entryRows).join('');
      if (!res.data.length) {
        empty.hidden = false;
        empty.querySelector('p').textContent = Object.keys(filters).length
          ? 'Không có thay đổi nào khớp bộ lọc.'
          : 'Chưa có thay đổi nào trong phạm vi của bạn.';
      }
      renderPaging(res.meta);
    } catch (err) {
      if (seq !== requestSeq) return;
      body.innerHTML = `<tr><td colspan="${COLUMNS}">Lỗi: ${esc(err.message)}</td></tr>`;
      container.querySelector('#logPaging').innerHTML = '';
    }
  }

  container.querySelectorAll('[data-filter]').forEach((control) => {
    control.addEventListener('change', async () => {
      const key = control.dataset.filter;
      if (control.value) filters[key] = control.value;
      else delete filters[key];
      if (key === 'instanceId') {
        // Người và trường của đơn vị trước không còn ý nghĩa ở đơn vị mới.
        for (const k of ['field', 'actorId', 'approverId']) delete filters[k];
        await loadFilterOptions();
      }
      load(1);
    });
  });

  container.querySelector('#logClear').addEventListener('click', async () => {
    const hadInstance = !!filters.instanceId;
    for (const k of Object.keys(filters)) delete filters[k];
    container.querySelectorAll('[data-filter]').forEach((control) => {
      control.value = '';
    });
    if (hadInstance) await loadFilterOptions();
    load(1);
  });

  container.querySelector('#logExport').addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Đang xuất...';
    try {
      const { blob, filename } = await api.masterDataChanges.exportCsv(toApiQuery(filters));
      downloadBlob(blob, filename);
      toast(`Đã tải xuống ${filename}`);
    } catch (err) {
      toast('Không xuất được: ' + err.message);
    } finally {
      btn.disabled = false;
      btn.innerHTML = label;
    }
  });

  await loadFilterOptions();
  load(currentPage);
}
